import React, { useState, useEffect, useMemo } from "react";
import {
  Settings,
  Users,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  BookOpen,
  Bus,
  Utensils,
  Library,
  Download,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Bell,
  Database,
  Activity,
  PieChart,
  LineChart,
  X,
  Save,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Calendar,
} from "lucide-react";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedTimeRange, setSelectedTimeRange] = useState("7d");
  const [filteredActivities, setFilteredActivities] = useState([]);

  // Modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showViewUserModal, setShowViewUserModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "student",
    phone: "",
    address: "",
    status: "active",
  });
  const [editUser, setEditUser] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState("7d");
  const [logFilter, setLogFilter] = useState("all");

  // Settings states
  const [settings, setSettings] = useState({
    schoolName: "GISS International School",
    timeZone: "UTC+3",
    language: "English",
    sessionTimeout: "30",
    passwordPolicy: "strong",
    twoFactorAuth: true,
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
  });

  // Data states
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);

  // Initialize data
  useEffect(() => {
    initializeData();
    loadSettings();
  }, []);

  const initializeData = () => {
    // Initialize with mock users data
    const mockUsers = [
      {
        id: 1,
        name: "System Administrator",
        email: "admin@giss.edu",
        role: "admin",
        phone: "+971 50 123 4567",
        address: "Dubai, UAE",
        status: "active",
        lastActive: "2 minutes ago",
        avatar: "👨‍💼",
      },
      {
        id: 2,
        name: "Sarah Johnson",
        email: "librarian@giss.edu",
        role: "librarian",
        phone: "+971 50 234 5678",
        address: "Dubai, UAE",
        status: "active",
        lastActive: "5 minutes ago",
        avatar: "👩‍💼",
      },
      {
        id: 3,
        name: "Michael Brown",
        email: "teacher@giss.edu",
        role: "teacher",
        phone: "+971 50 345 6789",
        address: "Dubai, UAE",
        status: "active",
        lastActive: "10 minutes ago",
        avatar: "👨‍🏫",
      },
      {
        id: 4,
        name: "Emma Wilson",
        email: "student@giss.edu",
        role: "student",
        phone: "+971 50 456 7890",
        address: "Dubai, UAE",
        status: "active",
        lastActive: "15 minutes ago",
        avatar: "👩‍🎓",
      },
      {
        id: 5,
        name: "James Smith",
        email: "parent@giss.edu",
        role: "parent",
        phone: "+971 50 567 8901",
        address: "Dubai, UAE",
        status: "active",
        lastActive: "1 hour ago",
        avatar: "👨‍👩‍👧‍👦",
      },
    ];
    setUsers(mockUsers);

    // Initialize system alerts
    const mockAlerts = [
      {
        id: 1,
        type: "warning",
        title: "Route 2 Delayed",
        message: "Bus Route 2 is running 15 minutes behind schedule",
        timestamp: "5 min ago",
        priority: "high",
        acknowledged: false,
        dismissed: false,
      },
      {
        id: 2,
        type: "info",
        title: "Library Closing Soon",
        message: "Library will close in 30 minutes",
        timestamp: "10 min ago",
        priority: "medium",
        acknowledged: false,
        dismissed: false,
      },
      {
        id: 3,
        type: "success",
        title: "System Update Complete",
        message: "All systems have been updated successfully",
        timestamp: "2 hours ago",
        priority: "low",
        acknowledged: true,
        dismissed: false,
      },
    ];
    setSystemAlerts(mockAlerts);
  };

  const loadSettings = () => {
    const savedSettings = localStorage.getItem("adminSettings");
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  };

  const saveSettings = () => {
    localStorage.setItem("adminSettings", JSON.stringify(settings));
    addNotification("Settings saved successfully", "success");
  };

  const addNotification = (message, type = "info") => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date().toLocaleTimeString(),
    };
    setNotifications((prev) => [notification, ...prev.slice(0, 4)]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    }, 5000);
  };

  const systemStats = [
    {
      title: "Total Users",
      value: users.length.toString(),
      change: "+12%",
      icon: Users,
      color: "blue",
      trend: "up",
    },
    {
      title: "Active Users",
      value: users.filter((user) => user.status === "active").length.toString(),
      change: "+8%",
      icon: BookOpen,
      color: "green",
      trend: "up",
    },
    {
      title: "Bus Routes",
      value: "15",
      change: "0%",
      icon: Bus,
      color: "purple",
      trend: "stable",
    },
    {
      title: "Daily Revenue",
      value: "$2,340",
      change: "+15%",
      icon: Utensils,
      color: "orange",
      trend: "up",
    },
    {
      title: "Digital Resources",
      value: "1,456",
      change: "+23%",
      icon: Library,
      color: "indigo",
      trend: "up",
    },
    {
      title: "System Health",
      value: "99.9%",
      change: "+0.1%",
      icon: Activity,
      color: "green",
      trend: "up",
    },
  ];

  const recentActivities = useMemo(
    () => [
      {
        id: 1,
        type: "library",
        user: "Emma Wilson",
        action: "borrowed Advanced Mathematics",
        timestamp: "2 minutes ago",
        status: "success",
      },
      {
        id: 2,
        type: "transportation",
        user: "Bus Route 3",
        action: "arrived at school",
        timestamp: "5 minutes ago",
        status: "info",
      },
      {
        id: 3,
        type: "cafeteria",
        user: "James Smith",
        action: "purchased lunch - $8.50",
        timestamp: "8 minutes ago",
        status: "success",
      },
      {
        id: 4,
        type: "e-library",
        user: "Sarah Davis",
        action: "downloaded Physics Lab Video",
        timestamp: "12 minutes ago",
        status: "success",
      },
      {
        id: 5,
        type: "system",
        user: "System",
        action: "backup completed successfully",
        timestamp: "1 hour ago",
        status: "info",
      },
    ],
    []
  );

  // Filter activities based on time range
  useEffect(() => {
    let filtered = recentActivities;

    switch (selectedTimeRange) {
      case "24h":
        // Show activities from last 24 hours (mock data)
        filtered = recentActivities.filter(
          (activity) =>
            activity.timestamp.includes("minutes ago") ||
            activity.timestamp.includes("hour ago")
        );
        break;
      case "7d":
        // Show activities from last 7 days (all mock data)
        filtered = recentActivities;
        break;
      case "30d":
        // Show activities from last 30 days (all mock data)
        filtered = recentActivities;
        break;
      case "90d":
        // Show activities from last 90 days (all mock data)
        filtered = recentActivities;
        break;
      default:
        filtered = recentActivities;
    }

    setFilteredActivities(filtered);
  }, [selectedTimeRange, recentActivities]);

  // Calculate dynamic user counts
  const getUserCountByRole = (role) => {
    return users.filter((user) => user.role === role.toLowerCase()).length;
  };

  const userRoles = [
    {
      role: "Admin",
      count: getUserCountByRole("admin"),
      permissions: [
        "Full system access",
        "User management",
        "System configuration",
      ],
      color: "red",
    },
    {
      role: "Librarian",
      count: getUserCountByRole("librarian"),
      permissions: ["Library management", "Book cataloging", "Loan processing"],
      color: "blue",
    },
    {
      role: "Teacher",
      count: getUserCountByRole("teacher"),
      permissions: [
        "Student borrowing",
        "Transportation view",
        "E-library access",
      ],
      color: "green",
    },
    {
      role: "Student",
      count: getUserCountByRole("student"),
      permissions: ["View own data", "E-library access", "Cafeteria purchases"],
      color: "purple",
    },
    {
      role: "Parent",
      count: getUserCountByRole("parent"),
      permissions: [
        "Child monitoring",
        "Cafeteria controls",
        "Transportation alerts",
      ],
      color: "orange",
    },
  ];

  const tabs = [
    { id: "overview", label: "System Overview", icon: BarChart3 },
    { id: "users", label: "User Management", icon: Users },
    { id: "analytics", label: "Analytics", icon: TrendingUp },
    { id: "settings", label: "System Settings", icon: Settings },
    { id: "logs", label: "Activity Logs", icon: Database },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case "library":
        return <BookOpen size={16} className="text-blue-500" />;
      case "transportation":
        return <Bus size={16} className="text-green-500" />;
      case "cafeteria":
        return <Utensils size={16} className="text-orange-500" />;
      case "e-library":
        return <Library size={16} className="text-purple-500" />;
      case "system":
        return <Settings size={16} className="text-gray-500" />;
      default:
        return <Activity size={16} className="text-gray-500" />;
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "warning":
        return <AlertTriangle size={20} className="text-yellow-500" />;
      case "info":
        return <Bell size={20} className="text-blue-500" />;
      case "success":
        return <CheckCircle size={20} className="text-green-500" />;
      default:
        return <Bell size={20} className="text-gray-500" />;
    }
  };

  // Form validation
  const validateForm = (userData) => {
    const errors = {};
    if (!userData.name.trim()) errors.name = "Name is required";
    if (!userData.email.trim()) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(userData.email))
      errors.email = "Email is invalid";
    if (!userData.role) errors.role = "Role is required";
    return errors;
  };

  // User management handlers
  const handleAddUser = () => {
    setNewUser({
      name: "",
      email: "",
      role: "student",
      phone: "",
      address: "",
      status: "active",
    });
    setFormErrors({});
    setShowAddUserModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddUserModal(false);
    setFormErrors({});
  };

  const handleEditUser = (user) => {
    setEditUser({ ...user });
    setFormErrors({});
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditUserModal(false);
    setFormErrors({});
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewUserModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirmModal(true);
  };

  const handleSaveUser = () => {
    const errors = validateForm(newUser);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Check if email already exists
    const emailExists = users.some(
      (user) => user.email.toLowerCase() === newUser.email.toLowerCase()
    );
    if (emailExists) {
      setFormErrors({ email: "Email already exists" });
      return;
    }

    const userToAdd = {
      ...newUser,
      id: Date.now(),
      lastActive: "Just now",
      avatar: "👤",
    };

    setUsers((prev) => [...prev, userToAdd]);
    setShowAddUserModal(false);
    setNewUser({
      name: "",
      email: "",
      role: "student",
      phone: "",
      address: "",
      status: "active",
    });
    setFormErrors({});
    addNotification(`User ${newUser.name} added successfully`, "success");
  };

  const handleUpdateUser = () => {
    const errors = validateForm(editUser);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Check if email already exists (excluding current user)
    const emailExists = users.some(
      (user) =>
        user.email.toLowerCase() === editUser.email.toLowerCase() &&
        user.id !== editUser.id
    );
    if (emailExists) {
      setFormErrors({ email: "Email already exists" });
      return;
    }

    setUsers((prev) =>
      prev.map((user) =>
        user.id === editUser.id ? { ...editUser, lastActive: "Just now" } : user
      )
    );
    setShowEditUserModal(false);
    setFormErrors({});
    addNotification(`User ${editUser.name} updated successfully`, "success");
  };

  const handleConfirmDelete = () => {
    // Prevent deletion of admin users
    if (selectedUser.role === "admin") {
      addNotification("Cannot delete admin users", "warning");
      setShowDeleteConfirmModal(false);
      return;
    }

    setUsers((prev) => prev.filter((user) => user.id !== selectedUser.id));
    setShowDeleteConfirmModal(false);
    addNotification(
      `User ${selectedUser.name} deleted successfully`,
      "success"
    );
  };

  // Search and filter handlers
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleRoleFilter = (role) => {
    setRoleFilter(role);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Export handlers
  const handleExportReport = () => {
    const csvContent = generateCSV(systemStats, "analytics_report");
    downloadCSV(csvContent, "analytics_report.csv");
    addNotification("Analytics report exported successfully", "success");
  };

  const handleExportLogs = () => {
    const csvContent = generateCSV(recentActivities, "activity_logs");
    downloadCSV(csvContent, "activity_logs.csv");
    addNotification("Activity logs exported successfully", "success");
  };

  const generateCSV = (data, type) => {
    if (type === "analytics_report") {
      return (
        "Metric,Value,Change\n" +
        data
          .map((stat) => `${stat.title},${stat.value},${stat.change}`)
          .join("\n")
      );
    } else if (type === "activity_logs") {
      return (
        "User,Action,Timestamp,Status\n" +
        data
          .map(
            (activity) =>
              `${activity.user},${activity.action},${activity.timestamp},${activity.status}`
          )
          .join("\n")
      );
    }
    return "";
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Settings handlers
  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Other handlers
  const handleViewAll = () => {
    setActiveTab("logs");
    addNotification("Switched to Activity Logs", "info");
  };

  const handleManageAlerts = () => {
    // Show alert management modal or switch to alerts tab
    addNotification("Alert management opened", "info");
  };

  const handleAcknowledgeAlert = (alertId) => {
    setSystemAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId ? { ...alert, acknowledged: true } : alert
      )
    );
    addNotification("Alert acknowledged", "success");
  };

  const handleDismissAlert = (alertId) => {
    setSystemAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId ? { ...alert, dismissed: true } : alert
      )
    );
    addNotification("Alert dismissed", "info");
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case "Add User":
        handleAddUser();
        break;
      case "System Config":
        setActiveTab("settings");
        addNotification("Opened System Settings", "info");
        break;
      case "Export Data":
        handleExportReport();
        break;
      case "Send Alert":
        // Create a new system alert
        const newAlert = {
          id: Date.now(),
          type: "info",
          title: "Manual Alert",
          message: "Alert sent by administrator",
          timestamp: "Just now",
          priority: "medium",
          acknowledged: false,
          dismissed: false,
        };
        setSystemAlerts((prev) => [newAlert, ...prev]);
        addNotification("Alert sent successfully", "success");
        break;
      default:
        addNotification(`${action} action initiated`, "info");
    }
  };

  const handleViewUsers = (role) => {
    setRoleFilter(role.toLowerCase());
    setActiveTab("users");
    addNotification(`Filtering users by role: ${role}`, "info");
  };

  const handleEditRole = (role) => {
    // For now, show a notification with role details
    const roleData = userRoles.find((r) => r.role === role);
    if (roleData) {
      addNotification(
        `Editing ${role} role - ${roleData.permissions.length} permissions`,
        "info"
      );
      // In a real app, this would open a role editing modal
    }
  };

  const renderSystemOverview = () => (
    <div className="overview-section">
      <div className="overview-header">
        <h3>System Overview</h3>
        <div className="time-range-selector">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="form-select"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        {systemStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card stat-${stat.color}`}>
              <div className="stat-icon">
                <Icon size={24} />
              </div>
              <div className="stat-content">
                <h3>{stat.value}</h3>
                <p>{stat.title}</p>
                <span className={`stat-change stat-change-${stat.trend}`}>
                  {stat.change}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="overview-grid">
        <div className="overview-card">
          <div className="card-header">
            <h4>Recent System Activity</h4>
            <button className="btn btn-outline btn-sm" onClick={handleViewAll}>
              View All
            </button>
          </div>
          <div className="activity-list">
            {filteredActivities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity-content">
                  <p className="activity-text">
                    <strong>{activity.user}</strong> {activity.action}
                  </p>
                  <span className="activity-time">{activity.timestamp}</span>
                </div>
                <div
                  className={`activity-status status-${activity.status}`}
                ></div>
              </div>
            ))}
          </div>
        </div>

        <div className="overview-card">
          <div className="card-header">
            <h4>System Alerts</h4>
            <button
              className="btn btn-outline btn-sm"
              onClick={handleManageAlerts}
            >
              Manage
            </button>
          </div>
          <div className="alerts-list">
            {systemAlerts
              .filter((alert) => !alert.dismissed)
              .map((alert) => (
                <div
                  key={alert.id}
                  className={`alert-item alert-${alert.type}`}
                >
                  <div className="alert-icon">{getAlertIcon(alert.type)}</div>
                  <div className="alert-content">
                    <h5>{alert.title}</h5>
                    <p>{alert.message}</p>
                    <span className="alert-time">{alert.timestamp}</span>
                    {alert.acknowledged && (
                      <span
                        className="alert-status"
                        style={{ color: "#10b981", fontSize: "0.75rem" }}
                      >
                        ✓ Acknowledged
                      </span>
                    )}
                  </div>
                  <div className="alert-actions">
                    <div
                      className={`alert-priority priority-${alert.priority}`}
                    >
                      {alert.priority}
                    </div>
                    {!alert.acknowledged && (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handleAcknowledgeAlert(alert.id)}
                        style={{
                          marginTop: "0.5rem",
                          fontSize: "0.75rem",
                          padding: "0.25rem 0.5rem",
                        }}
                      >
                        Acknowledge
                      </button>
                    )}
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleDismissAlert(alert.id)}
                      style={{
                        marginTop: "0.25rem",
                        fontSize: "0.75rem",
                        padding: "0.25rem 0.5rem",
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="overview-grid">
        <div className="overview-card">
          <div className="card-header">
            <h4>Module Status</h4>
          </div>
          <div className="module-status">
            <div className="module-item">
              <div className="module-info">
                <BookOpen size={20} />
                <span>Library Management</span>
              </div>
              <div className="module-status-badge status-online">Online</div>
            </div>
            <div className="module-item">
              <div className="module-info">
                <Bus size={20} />
                <span>Transportation</span>
              </div>
              <div className="module-status-badge status-online">Online</div>
            </div>
            <div className="module-item">
              <div className="module-info">
                <Utensils size={20} />
                <span>Cafeteria System</span>
              </div>
              <div className="module-status-badge status-online">Online</div>
            </div>
            <div className="module-item">
              <div className="module-info">
                <Library size={20} />
                <span>E-Library</span>
              </div>
              <div className="module-status-badge status-online">Online</div>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <div className="card-header">
            <h4>Quick Actions</h4>
          </div>
          <div className="quick-actions">
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction("Add User")}
            >
              <Users size={20} />
              <span>Add User</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction("System Config")}
            >
              <Settings size={20} />
              <span>System Config</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction("Export Data")}
            >
              <Download size={20} />
              <span>Export Data</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction("Send Alert")}
            >
              <Bell size={20} />
              <span>Send Alert</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUserManagement = () => (
    <div className="users-section">
      <div className="section-header">
        <h3>User Management</h3>
      </div>

      <div className="user-roles-grid">
        {userRoles.map((role, index) => (
          <div key={index} className={`role-card role-${role.color}`}>
            <div className="role-header">
              <h4>{role.role}</h4>
              <span className="role-count">{role.count} users</span>
            </div>
            <div className="role-permissions">
              <h5>Permissions:</h5>
              <ul>
                {role.permissions.map((permission, idx) => (
                  <li key={idx}>{permission}</li>
                ))}
              </ul>
            </div>
            <div className="role-actions">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleViewUsers(role.role)}
              >
                View Users
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleEditRole(role.role)}
              >
                Edit Role
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="header-actions">
        <div className="search-bar">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search users..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={handleAddUser}>
          <Plus size={16} />
          Add User
        </button>
      </div>

      <div className="users-table-section">
        <div className="table-header">
          <h4>All Users</h4>
          <div className="table-filters">
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => handleRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="librarian">Librarian</option>
              <option value="teacher">Teacher</option>
              <option value="student">Student</option>
              <option value="parent">Parent</option>
            </select>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{user.avatar}</div>
                        <div className="user-info">
                          <strong>{user.name}</strong>
                          <span>{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${user.status}`}>
                        {user.status.charAt(0).toUpperCase() +
                          user.status.slice(1)}
                      </span>
                    </td>
                    <td>{user.lastActive}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn"
                          onClick={() => handleViewUser(user)}
                          title="View User"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleEditUser(user)}
                          title="Edit User"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className={`action-btn ${
                            user.role === "admin" ? "disabled" : ""
                          }`}
                          onClick={() =>
                            user.role !== "admin" && handleDeleteUser(user)
                          }
                          title={
                            user.role === "admin"
                              ? "Cannot delete admin users"
                              : "Delete User"
                          }
                          disabled={user.role === "admin"}
                          style={{
                            opacity: user.role === "admin" ? 0.5 : 1,
                            cursor:
                              user.role === "admin" ? "not-allowed" : "pointer",
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#64748b",
                    }}
                  >
                    No users found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="analytics-section">
      <div className="section-header">
        <h3>System Analytics</h3>
        <div className="analytics-filters">
          <select
            className="form-select"
            value={analyticsTimeRange}
            onChange={(e) => setAnalyticsTimeRange(e.target.value)}
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <button className="btn btn-primary" onClick={handleExportReport}>
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card">
          <h4>Usage Statistics</h4>
          <div className="chart-placeholder">
            <LineChart size={48} />
            <p>Usage Trends Chart</p>
          </div>
        </div>

        <div className="analytics-card">
          <h4>Module Distribution</h4>
          <div className="chart-placeholder">
            <PieChart size={48} />
            <p>Module Usage Pie Chart</p>
          </div>
        </div>

        <div className="analytics-card">
          <h4>Performance Metrics</h4>
          <div className="metrics-list">
            <div className="metric-item">
              <span>Response Time</span>
              <span>245ms</span>
            </div>
            <div className="metric-item">
              <span>Uptime</span>
              <span>99.9%</span>
            </div>
            <div className="metric-item">
              <span>Error Rate</span>
              <span>0.01%</span>
            </div>
            <div className="metric-item">
              <span>Active Sessions</span>
              <span>156</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="settings-section">
      <div className="section-header">
        <h3>System Settings</h3>
        <button className="btn btn-primary" onClick={saveSettings}>
          <Save size={16} />
          Save Settings
        </button>
      </div>

      <div className="settings-grid">
        <div className="settings-card">
          <h4>General Settings</h4>
          <div className="setting-item">
            <label>School Name</label>
            <input
              type="text"
              value={settings.schoolName}
              onChange={(e) =>
                handleSettingChange("schoolName", e.target.value)
              }
              className="form-input"
            />
          </div>
          <div className="setting-item">
            <label>Time Zone</label>
            <select
              className="form-select"
              value={settings.timeZone}
              onChange={(e) => handleSettingChange("timeZone", e.target.value)}
            >
              <option value="UTC+3">UTC+3 (Dubai)</option>
              <option value="UTC+0">UTC+0 (London)</option>
              <option value="UTC-5">UTC-5 (New York)</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Language</label>
            <select
              className="form-select"
              value={settings.language}
              onChange={(e) => handleSettingChange("language", e.target.value)}
            >
              <option value="English">English</option>
              <option value="Arabic">Arabic</option>
            </select>
          </div>
        </div>

        <div className="settings-card">
          <h4>Security Settings</h4>
          <div className="setting-item">
            <label>Session Timeout</label>
            <select
              className="form-select"
              value={settings.sessionTimeout}
              onChange={(e) =>
                handleSettingChange("sessionTimeout", e.target.value)
              }
            >
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Password Policy</label>
            <select
              className="form-select"
              value={settings.passwordPolicy}
              onChange={(e) =>
                handleSettingChange("passwordPolicy", e.target.value)
              }
            >
              <option value="strong">
                Strong (8+ chars, numbers, symbols)
              </option>
              <option value="medium">Medium (6+ chars, numbers)</option>
              <option value="basic">Basic (4+ chars)</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Two-Factor Authentication</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.twoFactorAuth}
                onChange={(e) =>
                  handleSettingChange("twoFactorAuth", e.target.checked)
                }
              />
              <span className="toggle-slider"></span>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h4>Notification Settings</h4>
          <div className="setting-item">
            <label>Email Notifications</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) =>
                  handleSettingChange("emailNotifications", e.target.checked)
                }
              />
              <span className="toggle-slider"></span>
            </div>
          </div>
          <div className="setting-item">
            <label>SMS Notifications</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.smsNotifications}
                onChange={(e) =>
                  handleSettingChange("smsNotifications", e.target.checked)
                }
              />
              <span className="toggle-slider"></span>
            </div>
          </div>
          <div className="setting-item">
            <label>Push Notifications</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.pushNotifications}
                onChange={(e) =>
                  handleSettingChange("pushNotifications", e.target.checked)
                }
              />
              <span className="toggle-slider"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivityLogs = () => (
    <div className="logs-section">
      <div className="section-header">
        <h3>System Activity Logs</h3>
        <div className="logs-filters">
          <select
            className="form-select"
            value={logFilter}
            onChange={(e) => setLogFilter(e.target.value)}
          >
            <option value="all">All Activities</option>
            <option value="login">Login/Logout</option>
            <option value="data">Data Changes</option>
            <option value="system">System Events</option>
          </select>
          <button className="btn btn-outline" onClick={handleExportLogs}>
            <Download size={16} />
            Export Logs
          </button>
        </div>
      </div>

      <div className="logs-table">
        <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Module</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>2024-01-15 14:30:25</td>
              <td>admin@giss.edu</td>
              <td>User login</td>
              <td>Authentication</td>
              <td>
                <span className="status-badge status-success">Success</span>
              </td>
            </tr>
            <tr>
              <td>2024-01-15 14:28:15</td>
              <td>librarian@giss.edu</td>
              <td>Book added</td>
              <td>Library</td>
              <td>
                <span className="status-badge status-success">Success</span>
              </td>
            </tr>
            <tr>
              <td>2024-01-15 14:25:42</td>
              <td>teacher@giss.edu</td>
              <td>Student checkout</td>
              <td>Library</td>
              <td>
                <span className="status-badge status-success">Success</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  // Modal components
  const renderModals = () => (
    <>
      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="modal-close" onClick={handleCloseAddModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) =>
                    setNewUser({ ...newUser, name: e.target.value })
                  }
                  className={`form-input ${formErrors.name ? "error" : ""}`}
                  placeholder="Enter full name"
                />
                {formErrors.name && (
                  <span className="error-message">{formErrors.name}</span>
                )}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  className={`form-input ${formErrors.email ? "error" : ""}`}
                  placeholder="Enter email address"
                />
                {formErrors.email && (
                  <span className="error-message">{formErrors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({ ...newUser, role: e.target.value })
                  }
                  className={`form-select ${formErrors.role ? "error" : ""}`}
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="librarian">Librarian</option>
                  <option value="parent">Parent</option>
                  <option value="admin">Admin</option>
                </select>
                {formErrors.role && (
                  <span className="error-message">{formErrors.role}</span>
                )}
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) =>
                    setNewUser({ ...newUser, phone: e.target.value })
                  }
                  className="form-input"
                  placeholder="Enter phone number"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={newUser.address}
                  onChange={(e) =>
                    setNewUser({ ...newUser, address: e.target.value })
                  }
                  className="form-input"
                  placeholder="Enter address"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={handleCloseAddModal}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveUser}>
                <UserPlus size={16} />
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="modal-close" onClick={handleCloseEditModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={editUser.name || ""}
                  onChange={(e) =>
                    setEditUser({ ...editUser, name: e.target.value })
                  }
                  className={`form-input ${formErrors.name ? "error" : ""}`}
                />
                {formErrors.name && (
                  <span className="error-message">{formErrors.name}</span>
                )}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editUser.email || ""}
                  onChange={(e) =>
                    setEditUser({ ...editUser, email: e.target.value })
                  }
                  className={`form-input ${formErrors.email ? "error" : ""}`}
                />
                {formErrors.email && (
                  <span className="error-message">{formErrors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={editUser.role || ""}
                  onChange={(e) =>
                    setEditUser({ ...editUser, role: e.target.value })
                  }
                  className={`form-select ${formErrors.role ? "error" : ""}`}
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="librarian">Librarian</option>
                  <option value="parent">Parent</option>
                  <option value="admin">Admin</option>
                </select>
                {formErrors.role && (
                  <span className="error-message">{formErrors.role}</span>
                )}
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editUser.phone || ""}
                  onChange={(e) =>
                    setEditUser({ ...editUser, phone: e.target.value })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={editUser.address || ""}
                  onChange={(e) =>
                    setEditUser({ ...editUser, address: e.target.value })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editUser.status || "active"}
                  onChange={(e) =>
                    setEditUser({ ...editUser, status: e.target.value })
                  }
                  className="form-select"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={handleCloseEditModal}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdateUser}>
                <Save size={16} />
                Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {showViewUserModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>User Details</h3>
              <button
                className="modal-close"
                onClick={() => setShowViewUserModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                <div className="user-avatar-large">{selectedUser.avatar}</div>
                <div className="user-info-detail">
                  <h4>{selectedUser.name}</h4>
                  <p className="user-email">{selectedUser.email}</p>
                </div>
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <Mail size={16} />
                  <span>{selectedUser.email}</span>
                </div>
                <div className="detail-item">
                  <Phone size={16} />
                  <span>{selectedUser.phone || "Not provided"}</span>
                </div>
                <div className="detail-item">
                  <MapPin size={16} />
                  <span>{selectedUser.address || "Not provided"}</span>
                </div>
                <div className="detail-item">
                  <Calendar size={16} />
                  <span>Last active: {selectedUser.lastActive}</span>
                </div>
              </div>
              <div className="user-status">
                <span className={`role-badge role-${selectedUser.role}`}>
                  {selectedUser.role.charAt(0).toUpperCase() +
                    selectedUser.role.slice(1)}
                </span>
                <span className={`status-badge status-${selectedUser.status}`}>
                  {selectedUser.status.charAt(0).toUpperCase() +
                    selectedUser.status.slice(1)}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowViewUserModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowViewUserModal(false);
                  handleEditUser(selectedUser);
                }}
              >
                <Edit size={16} />
                Edit User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                className="modal-close"
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <AlertTriangle size={48} className="warning-icon" />
                <h4>Are you sure you want to delete this user?</h4>
                <p>
                  This action cannot be undone. The user{" "}
                  <strong>{selectedUser.name}</strong> will be permanently
                  removed from the system.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDelete}>
                <Trash2 size={16} />
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="notifications-container">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification notification-${notification.type}`}
            >
              <div className="notification-content">
                <span>{notification.message}</span>
                <small>{notification.timestamp}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <h1>Admin Dashboard</h1>
        <p>System administration, user management, and analytics</p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "overview" && renderSystemOverview()}
        {activeTab === "users" && renderUserManagement()}
        {activeTab === "analytics" && renderAnalytics()}
        {activeTab === "settings" && renderSystemSettings()}
        {activeTab === "logs" && renderActivityLogs()}
      </div>

      {renderModals()}
    </div>
  );
};

export default AdminDashboard;
